// src/pages/supplier/OrderDetail.jsx
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Truck, Package, CheckCircle, Clock, AlertTriangle,
  FileText, User, MapPin, Phone, Mail, ChevronRight, XCircle,
  CreditCard, Building2,
} from 'lucide-react'
import { supplierOrdersApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Button, Card, CardHeader, CardBody, StatusBadge, PageSpinner } from '@/components/common/ui'
import toast from 'react-hot-toast'

const STATUS_PIPELINE = [
  { key: 'confirmed',     label: 'Confirmed',     icon: CheckCircle },
  { key: 'processing',    label: 'Processing',    icon: Clock },
  { key: 'ready_to_ship', label: 'Ready to Ship', icon: Package },
  { key: 'shipped',       label: 'Shipped',       icon: Truck },
  { key: 'delivered',     label: 'Delivered',     icon: CheckCircle },
]

const STATUS_ACTIONS = {
  confirmed:     { next: 'processing',    label: 'Start Processing',   icon: Clock },
  processing:    { next: 'ready_to_ship', label: 'Mark Ready to Ship', icon: Package },
  ready_to_ship: { next: 'shipped',       label: 'Mark Shipped',       icon: Truck },
}

export default function SupplierOrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { format } = useCurrency()
  const [note, setNote] = useState('')
  const [shipForm, setShipForm] = useState({ courier_name: '', tracking_number: '', estimated_delivery_date: '' })
  const [showCancelForm, setShowCancelForm] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-order', id],
    queryFn: () => supplierOrdersApi.show(id),
  })

  const transition = useMutation({
    mutationFn: (statusData) => supplierOrdersApi.updateStatus(id, statusData),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Order updated!')
      qc.invalidateQueries({ queryKey: ['supplier-order', id] })
      qc.invalidateQueries({ queryKey: ['supplier-orders'] })
      setNote('')
      setShowCancelForm(false)
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to update order'),
  })

  const downloadInvoice = async () => {
    try {
      const res = await supplierOrdersApi.invoice(id)
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `invoice-${order?.order_number || id}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download invoice')
    }
  }

  if (isLoading) return <PageSpinner />

  const payload = data?.data?.data || {}
  const order = payload?.order || payload
  const allowedTransitions = payload?.allowed_transitions || []
  const sla = payload?.sla
  const gstBreakdown = payload?.gst_breakdown

  if (!order?.id) return <div className="text-center py-16 text-muted">Order not found.</div>

  const currentAction = STATUS_ACTIONS[order.status]
  const currentStepIdx = STATUS_PIPELINE.findIndex(s => s.key === order.status)
  const isCancelled = order.status === 'cancelled'
  const isDelivered = order.status === 'delivered'
  const canCancel = allowedTransitions.includes('cancelled')

  // Build shipping address from flat fields
  const hasShippingFields = order.shipping_name || order.shipping_address_line1

  return (
    <div className="max-w-4xl">
      {/* Back */}
      <button onClick={() => navigate('/supplier/orders')} className="flex items-center gap-1 text-sm text-muted hover:text-pink mb-4">
        <ArrowLeft size={14} /> Back to Orders
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-xl font-display font-bold flex items-center gap-2">
            {order.order_number}
            <StatusBadge status={order.status} />
          </h1>
          <p className="text-sm text-muted mt-1">
            Placed on {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={downloadInvoice}>
            <FileText size={14} /> Invoice
          </Button>
        </div>
      </div>

      {/* SLA Warning */}
      {sla && (
        <div className={`rounded-xl px-4 py-3 mb-4 flex items-center gap-3 text-sm ${sla.breached ? 'bg-red-50 border border-red-200 text-red-700' : sla.remaining_hrs <= 6 ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
          {sla.breached ? <AlertTriangle size={16} /> : <Clock size={16} />}
          <div className="flex-1">
            {sla.breached ? (
              <span className="font-bold">SLA breached — action overdue!</span>
            ) : (
              <span><strong>{sla.remaining_hrs}h remaining</strong> to move this order forward ({sla.sla_hours}h SLA for {sla.status.replace(/_/g, ' ')})</span>
            )}
          </div>
        </div>
      )}

      {/* Status Pipeline */}
      {!isCancelled && (
        <div className="bg-white border border-border rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between">
            {STATUS_PIPELINE.map((step, i) => {
              const isComplete = i < currentStepIdx || isDelivered
              const isCurrent = i === currentStepIdx && !isDelivered
              const Icon = step.icon
              return (
                <div key={step.key} className="flex items-center flex-1 last:flex-initial">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${isComplete ? 'bg-green-500 border-green-500 text-white' : isCurrent ? 'border-pink bg-pink/10 text-pink' : 'border-gray-200 bg-gray-50 text-gray-300'}`}>
                      {isComplete ? <CheckCircle size={16} /> : <Icon size={16} />}
                    </div>
                    <span className={`text-[10px] font-bold mt-1 text-center leading-tight ${isComplete ? 'text-green-600' : isCurrent ? 'text-gray-900' : 'text-gray-300'}`}>
                      {step.label}
                    </span>
                  </div>
                  {i < STATUS_PIPELINE.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 mt-[-12px] ${i < currentStepIdx || isDelivered ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              )
            })}
          </div>

          {isDelivered && order.delivered_at && (
            <p className="text-center text-xs text-green-600 font-bold mt-4">
              Delivered on {new Date(order.delivered_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>
      )}

      {/* Cancelled banner */}
      {isCancelled && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <XCircle size={20} className="text-red-500" />
            <div>
              <p className="font-bold text-sm text-red-800">Order Cancelled</p>
              {order.cancellation_reason && <p className="text-xs text-red-600 mt-0.5">{order.cancellation_reason}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Action Card */}
      {currentAction && !isCancelled && (
        <Card className="mb-6 border-blue-200 bg-blue-50/30">
          <CardBody>
            {order.status === 'ready_to_ship' ? (
              <div>
                <p className="text-sm font-bold mb-3 flex items-center gap-2">
                  <Truck size={16} className="text-blue-600" /> Ship This Order
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-muted mb-1 block">Courier Name *</label>
                    <input className="input w-full" placeholder="e.g. Delhivery, DTDC"
                      value={shipForm.courier_name} onChange={e => setShipForm(f => ({ ...f, courier_name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted mb-1 block">Tracking Number *</label>
                    <input className="input w-full" placeholder="AWB / tracking number"
                      value={shipForm.tracking_number} onChange={e => setShipForm(f => ({ ...f, tracking_number: e.target.value }))} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-xs font-bold text-muted mb-1 block">Estimated Delivery</label>
                    <input className="input w-full" type="date"
                      value={shipForm.estimated_delivery_date} onChange={e => setShipForm(f => ({ ...f, estimated_delivery_date: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-muted mb-1 block">Note (optional)</label>
                    <input className="input w-full" placeholder="Any note for the buyer"
                      value={note} onChange={e => setNote(e.target.value)} />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button loading={transition.isPending}
                    disabled={!shipForm.courier_name || !shipForm.tracking_number}
                    onClick={() => transition.mutate({ status: 'shipped', note, ...shipForm })}>
                    <Truck size={14} /> Mark Shipped
                  </Button>
                  {canCancel && (
                    <Button variant="ghost" size="sm" onClick={() => setShowCancelForm(true)}>
                      <XCircle size={14} /> Cancel
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <currentAction.icon size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-sm">{currentAction.label}</p>
                    <p className="text-xs text-muted">Move this order to the next stage</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input className="input text-sm w-48" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} />
                  <Button loading={transition.isPending}
                    onClick={() => transition.mutate({ status: currentAction.next, note })}>
                    {currentAction.label} <ChevronRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Cancel form */}
      {showCancelForm && canCancel && (
        <Card className="mb-6 border-red-200 bg-red-50/30">
          <CardBody>
            <p className="text-sm font-bold text-red-800 mb-2">Cancel This Order</p>
            <textarea className="input w-full h-20 mb-3" placeholder="Reason for cancellation (required)..."
              value={cancelReason} onChange={e => setCancelReason(e.target.value)} />
            <div className="flex gap-2">
              <Button variant="danger" size="sm" loading={transition.isPending}
                disabled={!cancelReason.trim()}
                onClick={() => transition.mutate({ status: 'cancelled', note: cancelReason })}>
                Confirm Cancel
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setShowCancelForm(false); setCancelReason('') }}>Back</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Tracking Info (if shipped) */}
      {order.tracking_number && (
        <Card className="mb-4 border-indigo-200 bg-indigo-50/30">
          <CardBody>
            <div className="flex items-center gap-3">
              <Truck size={18} className="text-indigo-600" />
              <div>
                <p className="text-sm font-bold text-indigo-800">Shipment Details</p>
                <p className="text-xs text-indigo-600">
                  {order.courier_name} — <span className="font-mono font-bold">{order.tracking_number}</span>
                  {order.estimated_delivery_date && ` · Est. ${new Date(order.estimated_delivery_date).toLocaleDateString('en-IN')}`}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        {/* Items */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader><span className="font-bold text-sm">Items ({order.items?.length || 0})</span></CardHeader>
            <CardBody className="divide-y divide-border p-0">
              {order.items?.map(item => (
                <div key={item.id} className="flex gap-3 items-center p-4">
                  <div className="w-12 h-12 rounded-lg bg-surface border border-border flex-shrink-0 overflow-hidden">
                    {(item.product?.image || item.product_image || item.product?.primary_image_url) && (
                      <img src={item.product?.image || item.product_image || item.product?.primary_image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{item.product_name || item.product?.name}</p>
                    {item.variant?.color_name && <p className="text-xs text-muted">{item.variant.color_name}</p>}
                    <p className="text-xs text-muted">{item.quantity}m × {format(item.unit_price || item.price)}</p>
                  </div>
                  <p className="font-bold text-sm flex-shrink-0">{format(item.total_price || item.total || (item.quantity * (item.unit_price || item.price)))}</p>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Buyer Info */}
          <Card>
            <CardHeader><span className="font-bold text-sm flex items-center gap-2"><User size={14} /> Buyer</span></CardHeader>
            <CardBody className="space-y-2 text-sm">
              <p className="font-bold">{order.buyer?.business_name || order.buyer?.name}</p>
              {order.buyer?.business_name && order.buyer?.name && (
                <p className="text-xs text-muted">{order.buyer.name}</p>
              )}
              {order.buyer?.phone && (
                <p className="flex items-center gap-2 text-muted text-xs"><Phone size={12} /> {order.buyer.phone}</p>
              )}
              {order.buyer?.email && (
                <p className="flex items-center gap-2 text-muted text-xs"><Mail size={12} /> {order.buyer.email}</p>
              )}
              {order.billing_gstin && (
                <p className="text-xs text-muted font-mono mt-1">GSTIN: {order.billing_gstin}</p>
              )}
            </CardBody>
          </Card>

          {/* Shipping Address */}
          <Card>
            <CardHeader><span className="font-bold text-sm flex items-center gap-2"><MapPin size={14} /> Ship To</span></CardHeader>
            <CardBody className="text-xs text-gray-600">
              {hasShippingFields ? (
                <>
                  <p className="font-bold text-sm text-gray-900">{order.shipping_name}</p>
                  <p>{order.shipping_address_line1}</p>
                  {order.shipping_address_line2 && <p>{order.shipping_address_line2}</p>}
                  <p>{order.shipping_city}, {order.shipping_state} – {order.shipping_pincode}</p>
                  {order.shipping_phone && <p className="mt-1">{order.shipping_phone}</p>}
                </>
              ) : typeof order.shipping_address === 'object' ? (
                <>
                  <p className="font-bold text-sm text-gray-900">{order.shipping_address.full_name}</p>
                  <p>{order.shipping_address.address_line1}</p>
                  {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                  <p>{order.shipping_address.city}, {order.shipping_address.state} – {order.shipping_address.pincode}</p>
                  {order.shipping_address.phone && <p className="mt-1">{order.shipping_address.phone}</p>}
                </>
              ) : order.shipping_address ? (
                <p>{order.shipping_address}</p>
              ) : (
                <p className="text-muted">No shipping address</p>
              )}
            </CardBody>
          </Card>

          {/* Payment Info */}
          <Card>
            <CardHeader><span className="font-bold text-sm flex items-center gap-2"><CreditCard size={14} /> Payment</span></CardHeader>
            <CardBody className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">Method</span>
                <span className="font-bold capitalize">{order.payment_method?.replace(/_/g, ' ') || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Status</span>
                <StatusBadge status={order.payment_status || 'pending'} />
              </div>
              {order.paid_at && (
                <div className="flex justify-between">
                  <span className="text-muted">Paid on</span>
                  <span className="text-xs">{new Date(order.paid_at).toLocaleDateString('en-IN')}</span>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Order Summary with GST Breakdown */}
      <Card className="mb-4">
        <CardHeader><span className="font-bold text-sm">Order Summary</span></CardHeader>
        <CardBody className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-muted">Subtotal</span><span>{format(order.subtotal || 0)}</span></div>

          {/* GST Breakdown */}
          {gstBreakdown ? (
            <>
              {gstBreakdown.cgst > 0 && <div className="flex justify-between"><span className="text-muted">CGST ({gstBreakdown.cgst_rate || '9'}%)</span><span>{format(gstBreakdown.cgst)}</span></div>}
              {gstBreakdown.sgst > 0 && <div className="flex justify-between"><span className="text-muted">SGST ({gstBreakdown.sgst_rate || '9'}%)</span><span>{format(gstBreakdown.sgst)}</span></div>}
              {gstBreakdown.igst > 0 && <div className="flex justify-between"><span className="text-muted">IGST ({gstBreakdown.igst_rate || '18'}%)</span><span>{format(gstBreakdown.igst)}</span></div>}
            </>
          ) : order.gst_amount > 0 && (
            <div className="flex justify-between"><span className="text-muted">GST</span><span>{format(order.gst_amount)}</span></div>
          )}

          {order.shipping_charge > 0 && <div className="flex justify-between"><span className="text-muted">Shipping</span><span>{format(order.shipping_charge)}</span></div>}
          {order.discount_amount > 0 && <div className="flex justify-between text-green-600"><span>Discount {order.coupon_code && `(${order.coupon_code})`}</span><span>−{format(order.discount_amount)}</span></div>}
          <div className="flex justify-between font-extrabold pt-1.5 border-t border-border text-base">
            <span>Total</span><span>{format(order.total_amount)}</span>
          </div>
        </CardBody>
      </Card>

      {/* Notes */}
      {(order.buyer_notes || order.supplier_notes) && (
        <Card className="mb-4">
          <CardHeader><span className="font-bold text-sm">Notes</span></CardHeader>
          <CardBody className="space-y-2 text-sm">
            {order.buyer_notes && (
              <div>
                <span className="text-xs font-bold text-muted uppercase">Buyer note:</span>
                <p className="text-gray-700">{order.buyer_notes}</p>
              </div>
            )}
            {order.supplier_notes && (
              <div>
                <span className="text-xs font-bold text-muted uppercase">Your note:</span>
                <p className="text-gray-700">{order.supplier_notes}</p>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Order Timeline */}
      {order.status_logs?.length > 0 && (
        <Card className="mb-4">
          <CardHeader><span className="font-bold text-sm">Order Timeline</span></CardHeader>
          <CardBody>
            <div className="relative">
              {order.status_logs.map((log, i) => {
                const isLast = i === order.status_logs.length - 1
                return (
                  <div key={i} className="flex gap-3 pb-4 last:pb-0">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isLast ? 'bg-pink ring-4 ring-pink/20' : 'bg-gray-300'}`} />
                      {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                    </div>
                    <div className="flex-1 min-w-0 -mt-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm capitalize">{(log.to_status || log.status)?.replace(/_/g, ' ')}</span>
                        {log.changed_by_role && <span className="text-[10px] bg-surface px-1.5 py-0.5 rounded capitalize">{log.changed_by_role}</span>}
                      </div>
                      {log.note && <p className="text-xs text-gray-600 mt-0.5">{log.note}</p>}
                      <p className="text-[10px] text-muted mt-0.5">{new Date(log.created_at).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Cancel action */}
      {canCancel && !showCancelForm && !['ready_to_ship'].includes(order.status) && (
        <div className="mt-4">
          <Button variant="ghost" size="sm" onClick={() => setShowCancelForm(true)} className="text-red-500 hover:text-red-700">
            <XCircle size={14} /> Cancel Order
          </Button>
        </div>
      )}
    </div>
  )
}
